import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';

interface RulesQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: string;
  setQuestion: (question: string) => void;
}

const RulesQuestionModal: React.FC<RulesQuestionModalProps> = ({
  isOpen,
  onClose,
  question,
  setQuestion
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isQuestionValid = question.trim().length >= 10;

  const handleClose = () => {
    if (!isSubmitting) {
      setQuestion('');
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!isQuestionValid) {
      return;
    }

    try {
      setIsSubmitting(true);

      // TODO: call API to send rules question to coworker
      // await apiClient.post('/rules/question', { question: question.trim() });

      toast({
        title: 'Question sent',
        description: 'Your rules question has been sent.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setQuestion('');
      onClose();
    } catch (error: any) {
      console.error('Error sending rules question:', error);
      toast({
        title: 'Unable to send question',
        description: error?.response?.data?.message || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent
        bg={bgColor}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="xl"
        boxShadow="2xl"
        maxH="90vh"
        overflowY="auto"
      >
        <ModalHeader color={textColor}>
          Rules Question
        </ModalHeader>
        <ModalCloseButton color={textColor} />
        
        <ModalBody>
          <FormControl isRequired>
            <FormLabel color={textColor}>What's your question about the rules?</FormLabel>
            <Textarea
              placeholder="What's your question about the rules?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={6}
              resize="vertical"
              color={textColor}
              borderColor={borderColor}
              _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
              _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!isQuestionValid || isSubmitting}
          >
            Send Question
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RulesQuestionModal;

